'use client';

import { useCallback } from 'react';
import {
  useGetAccountUsageQuery,
  useGetCanManageBillingPermissionQuery,
  useGetPlansLazyQuery,
} from '@frameio/graphql-client/generated';
import { usePlansForSelectedPeriod } from '@frameio/plan-management/plans/hooks/usePlansForSelectedPeriod';
import { getUpgradedPlan } from '@frameio/plan-management/plans/utils';
import { useActiveAccountId } from '@frameio/shared/contexts';
import { useIsMountableStorageEnabled } from '@frameio/shared/plans/hooks/useIsMountableStorageEnabled';
import { EMPTY_ARRAY } from '@frameio/utils';
import { ArchivalStorageAlertBanner } from './ArchivalStorageAlertBanner';
import { GuestLimitAlertBanner } from './GuestLimitAlertBanner';
import { LegacyStorageLimitAlertBanner } from './LegacyStorageLimitAlertBanner';
import { MemberLimitAlertBanner } from './MemberLimitAlertBanner';
import { MountedStorageAlertBanner } from './MountedStorageAlertBanner';
import { PaymentAlertBanner } from './PaymentAlertBanner';
import { StorageLimitAlertBanner } from './StorageLimitAlertBanner';
import { TrialAlertBanner } from './TrialAlertBanner';
import { WorkspaceAlertBanner } from './WorkspaceAlertBanner';

export const UsageAlertBanners = () => {
  const accountId = useActiveAccountId();

  const { data } = useGetAccountUsageQuery({
    variables: { accountId: accountId ?? '' },
    skip: !accountId,
  });

  const { loading: arePermissionsLoading } =
    useGetCanManageBillingPermissionQuery({
      variables: { accountId: accountId ?? '' },
      skip: !accountId,
    });

  const account = data?.account;
  const subscription = account?.subscription;
  const planId = subscription?.plan?.id;

  const [plansQuery, plansData] = useGetPlansLazyQuery({
    variables: { accountId: accountId ?? '' },
  });

  const fetchPlansData = useCallback(() => {
    if (!planId) return;
    plansQuery();
  }, [planId, plansQuery]);

  const plans = plansData?.data?.account?.plans ?? EMPTY_ARRAY;
  const period = subscription?.period ?? 'MONTHLY';
  const isYearlySelected = period === 'YEARLY';
  const tier = subscription?.tier ?? 'PRO';

  const plansForSelectedPeriod = usePlansForSelectedPeriod(
    plans,
    isYearlySelected
  );

  const isLegacyPlan = subscription?.isLegacy ?? false;

  // For STARTER tier: treat like FREE so getUpgradedPlan finds the PRO plan.
  // This upgradedPlan is shared across all banners, so STARTER accounts will
  // see "Subscribe to Pro" everywhere — which is the correct upgrade path.
  const effectiveTierForUpgrade = tier === 'STARTER' ? 'FREE' : tier;
  const upgradedPlan =
    getUpgradedPlan(plansForSelectedPeriod, effectiveTierForUpgrade) ??
    undefined;

  const isMountedStorageBannerEnabled = useIsMountableStorageEnabled();

  if (!account || arePermissionsLoading) return null;

  return (
    <>
      {isLegacyPlan ? (
        <LegacyStorageLimitAlertBanner
          account={account}
          fetchPlansData={fetchPlansData}
          upgradedPlan={upgradedPlan}
        />
      ) : (
        <StorageLimitAlertBanner
          account={account}
          fetchPlansData={fetchPlansData}
          upgradedPlan={upgradedPlan}
        />
      )}

      <ArchivalStorageAlertBanner account={account} />
      {isMountedStorageBannerEnabled && (
        <MountedStorageAlertBanner account={account} />
      )}

      <MemberLimitAlertBanner
        account={account}
        fetchPlansData={fetchPlansData}
        upgradedPlan={upgradedPlan}
      />

      <GuestLimitAlertBanner
        account={account}
        fetchPlansData={fetchPlansData}
        upgradedPlan={upgradedPlan}
      />

      <PaymentAlertBanner account={account} />

      <TrialAlertBanner
        account={account}
        fetchPlansData={fetchPlansData}
        upgradedPlan={upgradedPlan}
      />

      <WorkspaceAlertBanner account={account} />
    </>
  );
};
